/**
 * scaleResolution — frontend mirror of backend/services/scale_resolution.py.
 *
 * GH-22 (real-plan certification, 2026-10-01) — the live measurement
 * preview (this module's caller, `useMeasurementController.js`) used
 * the single whole-sheet `sheetScale` for every on-canvas readout,
 * completely bypassing per-region calibration. The SAVE path
 * (`routers/measurements.py`) already calls the real
 * `resolve_scale_for_points` and correctly prefers a matching region
 * calibration over the global one. Operator experience: drag inside a
 * calibrated detail region, watch one number update live, release,
 * and the SAVED measurement shows a DIFFERENT number — the exact
 * region calibration workflow this platform exists to support looked
 * broken.
 *
 * Ported in JS (not called over the network) because the live preview
 * must update on every pointer move with no round-trip latency. Keep
 * this in lockstep with the Python source — same branches, same
 * tolerances, same precedence — any change there needs the matching
 * change here.
 */

const OVERLAP_AGREEMENT_TOLERANCE = 0.03;
const AXIS_TOLERANCE = 0.01;
const MULTI_SCALE_CONTEXTS = ["anisotropic_hv", "multi_viewport"];
const UNVERIFIED_WITHOUT_REGION_CONTEXTS = [...MULTI_SCALE_CONTEXTS, "unknown", null, undefined];

function pointInBbox(pt, bbox) {
  const [nx0, ny0, nx1, ny1] = bbox;
  const loX = Math.min(nx0, nx1), hiX = Math.max(nx0, nx1);
  const loY = Math.min(ny0, ny1), hiY = Math.max(ny0, ny1);
  return pt.nx >= loX && pt.nx <= hiX && pt.ny >= loY && pt.ny <= hiY;
}

function allPointsInBbox(points, bbox) {
  return !!points?.length && points.every((p) => pointInBbox(p, bbox));
}

function ftPerPxOf(scale) {
  if (!scale || scale.p1_nx == null || scale.p2_nx == null) return null;
  const w = scale.raster_width_px, h = scale.raster_height_px;
  if (!w || !h || !scale.real_distance_ft) return null;
  const dx = (scale.p2_nx - scale.p1_nx) * w;
  const dy = (scale.p2_ny - scale.p1_ny) * h;
  const basePx = Math.hypot(dx, dy);
  if (basePx <= 0) return null;
  return scale.real_distance_ft / basePx;
}

function regionsAgree(scales) {
  const ratios = scales.map(ftPerPxOf).filter((r) => r != null);
  if (ratios.length < 2) return true;
  const lo = Math.min(...ratios), hi = Math.max(...ratios);
  if (lo <= 0) return true;
  return (hi - lo) / lo <= OVERLAP_AGREEMENT_TOLERANCE;
}

function axisOf(p1, p2) {
  if (p1?.nx == null || p1?.ny == null || p2?.nx == null || p2?.ny == null) return null;
  const dx = Math.abs(p2.nx - p1.nx), dy = Math.abs(p2.ny - p1.ny);
  if (dx <= AXIS_TOLERANCE && dy > AXIS_TOLERANCE) return "vertical";
  if (dy <= AXIS_TOLERANCE && dx > AXIS_TOLERANCE) return "horizontal";
  return null;
}

function pointsAxis(points) {
  if (!points || points.length < 2) return null;
  let axis = null;
  for (let i = 1; i < points.length; i++) {
    const segAxis = axisOf(points[i - 1], points[i]);
    if (!segAxis) return null;
    if (!axis) axis = segAxis;
    else if (axis !== segAxis) return null;
  }
  return axis;
}

/**
 * Mirrors `resolve_scale_for_points` exactly. Returns
 * { scale, trust, reason } where trust is one of
 * "trusted" | "unverified" | "untrusted" | "none".
 */
export function resolveScaleForPoints(scales, points, sheetScaleContext) {
  const allScales = scales || [];
  const regionScales = allScales.filter((s) => s.region_bbox);
  const globalScales = allScales.filter((s) => !s.region_bbox);

  const matchingRegions = regionScales.filter((s) => allPointsInBbox(points, s.region_bbox));
  if (matchingRegions.length) {
    const measAxis = pointsAxis(points);
    if (measAxis && matchingRegions.length > 1) {
      const axisMatches = matchingRegions.filter(
        (s) => axisOf({ nx: s.p1_nx, ny: s.p1_ny }, { nx: s.p2_nx, ny: s.p2_ny }) === measAxis,
      );
      if (axisMatches.length === 1) return { scale: axisMatches[0], trust: "trusted", reason: null };
    }
    if (matchingRegions.length > 1 && !regionsAgree(matchingRegions)) {
      return {
        scale: matchingRegions[0], trust: "unverified",
        reason: `${matchingRegions.length} scale regions on this sheet all cover the area you measured, and they disagree on the real-world scale — the value shown may be wrong. Review and consolidate the overlapping region calibrations for this area.`,
      };
    }
    const winner = matchingRegions[0];
    if (sheetScaleContext === "anisotropic_hv") {
      const calibAxis = axisOf({ nx: winner.p1_nx, ny: winner.p1_ny }, { nx: winner.p2_nx, ny: winner.p2_ny });
      const measAxis2 = pointsAxis(points);
      if (!calibAxis || !measAxis2 || calibAxis !== measAxis2) {
        return {
          scale: winner, trust: "unverified",
          reason: "This sheet has a genuinely different horizontal and vertical scale (e.g. an exaggerated profile view). A single region calibration can only be trusted along the exact axis it was itself measured on — this measurement isn't confidently on that same axis, so the value shown may be wrong.",
        };
      }
    }
    return { scale: winner, trust: "trusted", reason: null };
  }

  if (!globalScales.length) {
    if (regionScales.length) {
      return {
        scale: null, trust: "none",
        reason: "This sheet has region-specific scale calibrations, but none covers the area you measured. Calibrate this region before taking a trusted measurement here.",
      };
    }
    return { scale: null, trust: "none", reason: "This sheet has no scale calibration." };
  }

  const globalScale = globalScales[0];

  if (sheetScaleContext === "nts") {
    return {
      scale: globalScale, trust: "untrusted",
      reason: "This sheet is classified NOT TO SCALE. A calibration exists, but graphical measurements on an NTS sheet are not reliable by definition — proceed only if you know what you're doing.",
    };
  }

  if (MULTI_SCALE_CONTEXTS.includes(sheetScaleContext) && regionScales.length) {
    return {
      scale: globalScale, trust: "unverified",
      reason: "This sheet has multiple scale regions and the point you measured isn't inside any of them — the whole-sheet scale was used, but it may not be correct for this area.",
    };
  }

  if (UNVERIFIED_WITHOUT_REGION_CONTEXTS.includes(sheetScaleContext)) {
    const reason = MULTI_SCALE_CONTEXTS.includes(sheetScaleContext)
      ? "This sheet is classified as having more than one real scale (e.g. plan/profile or multiple viewports), but only a single whole-sheet calibration exists. This value may be wrong for this specific area — calibrate the region you're measuring."
      : "This sheet's scale layout could not be confirmed (unknown scale context) — a whole-sheet calibration exists and was used, but it has not been verified as correct everywhere on this sheet.";
    return { scale: globalScale, trust: "unverified", reason };
  }

  // R-13 (independent recheck batch 2, 2026-10-01) — same agreement
  // check FP-055 already applies to overlapping REGION calibrations,
  // applied to GLOBAL ones too, so two conflicting whole-sheet
  // calibrations are never silently picked by list order and reported
  // "trusted". Placed AFTER every context downgrade above (NTS's
  // harder-blocking "untrusted", and the multi-scale/unknown-context
  // "unverified" downgrades) so a sheet that already needs one of
  // THOSE protections keeps getting it — kept IDENTICAL to
  // services/scale_resolution.py.
  if (globalScales.length > 1 && !regionsAgree(globalScales)) {
    return {
      scale: globalScales[0], trust: "unverified",
      reason: `This sheet has ${globalScales.length} whole-sheet scale calibrations and they disagree on the real-world scale — the value shown may be wrong. Review and consolidate the duplicate calibrations for this sheet.`,
    };
  }

  return { scale: globalScale, trust: "trusted", reason: null };
}
